import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ExcelJS from 'exceljs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';
import ResponsiveTable from './ResponsiveTable';

function ByEachClientLatestMonthTable({
  data = [],
  totalCount,
  currentPage,
  pageSize,
}) {
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(null); // 다운로드 중인 calculateMonth

  const handleExcelDownload = useCallback(async (clientId, calculateMonth) => {
    setDownloading(calculateMonth);
    try {
      const res = await axios.get('/ByPlatform/excelDataByClient', {
        params: { clientId, calculateMonth },
      });
      const { rows, clientName } = res.data;
      if (!rows || rows.length === 0) { alert('데이터가 없습니다.'); return; }

      const C = {
        PLATFORM_FILL: { type:'pattern', pattern:'solid', fgColor:{ argb:'FF1E3A5F' } },
        HEADER_FILL:   { type:'pattern', pattern:'solid', fgColor:{ argb:'FF2D5986' } },
        ALBUM_FILL:    { type:'pattern', pattern:'solid', fgColor:{ argb:'FFE8E8E8' } },
        SUB_FILL:      { type:'pattern', pattern:'solid', fgColor:{ argb:'FF2D5986' } },
        TOTAL_FILL:    { type:'pattern', pattern:'solid', fgColor:{ argb:'FF1E3A5F' } },
        WHITE: { argb:'FFFFFFFF' }, BLACK: { argb:'FF000000' }, LIGHT: { argb:'FFCBD5E1' },
        BORDER: {
          top:{ style:'thin', color:{ argb:'FF334155' } }, left:{ style:'thin', color:{ argb:'FF334155' } },
          bottom:{ style:'thin', color:{ argb:'FF334155' } }, right:{ style:'thin', color:{ argb:'FF334155' } },
        },
        NUM: '#,##0',
      };

      const COLS = [
        { key:'media', header:'MEDIA', width:10 }, { key:'channel', header:'채널', width:20 },
        { key:'calculateMonth', header:'정산월', width:12 }, { key:'serviceMonth', header:'서비스월', width:12 },
        { key:'albumName', header:'앨범명', width:35 },
        { key:'albumCode', header:'앨범코드', width:12 }, { key:'songName', header:'트랙명', width:45 },
        { key:'songCode', header:'트랙코드', width:12 }, { key:'artistName', header:'아티스트명', width:16 },
        { key:'streaming', header:'스트리밍', width:14 },
        { key:'download', header:'다운로드', width:12 },
        { key:'amount', header:'저작인접권료', width:16 }, { key:'fee', header:'유통수수료', width:14 },
        { key:'royalty', header:'인세', width:16 },
      ];
      const NUM_COLS = new Set(['streaming','download','amount','fee','royalty']);
      const MERGE_END = 9;

      const DOMESTIC_ORDER = ['멜론', '바이브', '벅스', '지니', '플로'];
      const platformGroups = {};
      rows.forEach((r) => {
        const pg = DOMESTIC_ORDER.includes(r.platform) ? r.platform : '해외';
        if (!platformGroups[pg]) platformGroups[pg] = [];
        platformGroups[pg].push(r);
      });
      const orderedGroups = [
        ...DOMESTIC_ORDER.filter((p) => platformGroups[p]),
        ...(platformGroups['해외'] ? ['해외'] : []),
      ];

      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('플랫폼별정산');
      COLS.forEach((c, i) => { ws.getColumn(i + 1).width = c.width; });

      let curRow = 1;
      const styleCell = (cell, opts = {}) => {
        if (opts.fill) cell.fill = opts.fill;
        if (opts.font) cell.font = { size: opts.size || 10, ...opts.font };
        if (opts.align) cell.alignment = { vertical:'middle', ...opts.align };
        if (opts.border !== false) cell.border = C.BORDER;
        if (opts.numFmt) cell.numFmt = opts.numFmt;
      };
      const addRow = (values, opts = {}) => {
        const r = ws.getRow(curRow++);
        r.height = opts.height || 18;
        values.forEach((v, i) => {
          const cell = r.getCell(i + 1);
          cell.value = v;
          styleCell(cell, {
            fill: opts.fill,
            font: { bold: opts.bold, color: opts.color || C.BLACK, size: opts.size || 10 },
            align: { horizontal: opts.center ? 'center' : (NUM_COLS.has(COLS[i]?.key) ? 'right' : 'left') },
            numFmt: NUM_COLS.has(COLS[i]?.key) ? C.NUM : undefined,
          });
        });
        return r;
      };

      let totalStreaming = 0, totalDownload = 0, totalAmount = 0, totalFee = 0, totalRoyalty = 0;

      for (const pgName of orderedGroups) {
        const pgRows = platformGroups[pgName];
        const phRow = ws.getRow(curRow++);
        phRow.height = 30;
        ws.mergeCells(curRow - 1, 1, curRow - 1, COLS.length);
        const phCell = phRow.getCell(1);
        phCell.value = pgName;
        styleCell(phCell, { fill: C.PLATFORM_FILL, font: { bold: true, size: 16, color: C.WHITE }, align: { horizontal: 'left' }, border: false });

        addRow(COLS.map((c) => c.header), { fill: C.HEADER_FILL, bold: true, color: C.LIGHT, height: 20, center: true });

        const albumOrder = [];
        const albumRowMap = {};
        pgRows.forEach((r) => {
          if (!albumRowMap[r.albumId]) { albumRowMap[r.albumId] = []; albumOrder.push(r.albumId); }
          albumRowMap[r.albumId].push(r);
        });

        let pgS = 0, pgD = 0, pgA = 0, pgF = 0, pgR = 0;
        for (const albumId of albumOrder) {
          const albumRows = albumRowMap[albumId];
          let albS = 0, albD = 0, albA = 0, albF = 0, albR = 0;
          albumRows.forEach((r) => {
            addRow(['DIGITAL', r.channel, r.calculateMonth, r.serviceMonth, r.albumName, r.albumCode, r.songName, r.songCode, r.artistName, r.streaming, r.download, r.amount, r.fee, r.royalty], { color: C.BLACK });
            albS += r.streaming; albD += r.download;
            albA += r.amount;    albF += r.fee; albR += r.royalty;
          });
          addRow([`[소계] ${albumRows[0].albumName}`, '', '', '', '', '', '', '', '', albS, albD, albA, albF, albR], { fill: C.ALBUM_FILL, bold: true, color: C.BLACK });
          ws.mergeCells(curRow - 1, 1, curRow - 1, MERGE_END);
          pgS += albS; pgD += albD; pgA += albA; pgF += albF; pgR += albR;
        }
        addRow([`${pgName} 합계`, '', '', '', '', '', '', '', '', pgS, pgD, pgA, pgF, pgR], { fill: C.SUB_FILL, bold: true, color: C.WHITE, size: 11, height: 22 });
        ws.mergeCells(curRow - 1, 1, curRow - 1, MERGE_END);
        totalStreaming += pgS; totalDownload += pgD;
        totalAmount   += pgA; totalFee      += pgF; totalRoyalty  += pgR;
        curRow++;
      }

      curRow++;
      addRow(['총 합계', '', '', '', '', '', '', '', '', totalStreaming, totalDownload, totalAmount, totalFee, totalRoyalty], { fill: C.TOTAL_FILL, bold: true, color: C.WHITE, size: 13, height: 26 });
      ws.mergeCells(curRow - 1, 1, curRow - 1, MERGE_END);

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${clientName}_${calculateMonth.substring(0, 7)}_플랫폼별정산.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('엑셀 다운로드 오류:', err);
      alert('엑셀 다운로드 중 오류가 발생했습니다.');
    } finally {
      setDownloading(null);
    }
  }, []);

  const columns = React.useMemo(() => {
    const baseColumns = [
      {
        Header: () => <div className="whitespace-nowrap text-center">순번</div>,
        accessor: (_row, i) => totalCount - ((currentPage - 1) * pageSize + i),
        id: 'index',
        Cell: ({ value }) => (
          <div className="text-center whitespace-nowrap">{value}</div>
        ),
      },
      {
        Header: '정산월',
        accessor: 'calculateMonth',
        Cell: ({ row, value }) => (
          <span
            className="font-bold text-sky-600 hover:cursor-pointer text-sm"
            onClick={() =>
              navigate(
                `/ByClientMonthAlbums?clientId=${row.original.clientId}&calculateMonth=${row.original.calculateMonth}`
              )
            }>
            {value?.slice(0, 7)}
          </span>
        ),
      },
      {
        Header: () => <div className="text-center whitespace-nowrap">엑셀</div>,
        id: 'excel',
        Cell: ({ row }) => (
          <div className="flex justify-center">
            <button
              title="플랫폼별 정산 엑셀 다운로드"
              onClick={() => handleExcelDownload(row.original.clientId, row.original.calculateMonth)}
              disabled={downloading === row.original.calculateMonth}
              className="text-emerald-500 hover:text-emerald-400 disabled:opacity-40 transition-colors text-lg">
              {downloading === row.original.calculateMonth
                ? <span className="animate-spin inline-block w-4 h-4 border-2 border-t-emerald-400 border-emerald-400/20 rounded-full" />
                : <FontAwesomeIcon icon={faFileExcel} />}
            </button>
          </div>
        ),
      },
      {
        Header: '인접권료',
        accessor: 'amount',
        Cell: ({ value }) => Number(value).toLocaleString(),
      },
      {
        Header: '유통수수료',
        accessor: 'feeAmount',
        Cell: ({ value }) => Number(value).toLocaleString(),
      },
      {
        Header: '인세총액',
        accessor: 'receiveAmount',
        Cell: ({ value }) => Number(value).toLocaleString(),
      },
      ...(data.some((r) => Number(r.physicalAmount) > 0) ? [{
        Header: '음반정산',
        accessor: 'physicalAmount',
        Cell: ({ value }) =>
          value > 0 ? Number(value).toLocaleString() : <span className="text-slate-600">-</span>,
      }] : []),
      ...(data.some((r) => Number(r.commissionShareAmount) > 0) ? [{
        Header: '별도정산',
        accessor: 'commissionShareAmount',
        Cell: ({ value }) => Number(value || 0).toLocaleString(),
      }] : []),
      {
        Header: '전월이월',
        accessor: 'prevAmount',
        Cell: ({ value }) => Number(value).toLocaleString(),
      },
      ...(data.some((r) => Number(r.advanceAmount) > 0) ? [{
        Header: '선급',
        accessor: 'advanceAmount',
        Cell: ({ value }) =>
          value > 0 ? (
            <span className="text-rose-400">-{Number(value).toLocaleString()}</span>
          ) : (
            <span className="text-slate-600">-</span>
          ),
      }] : []),
      {
        Header: '지급총액',
        id: 'combinedTotal',
        Cell: ({ row }) => {
          const total = (row.original.totalAmount || 0) + (row.original.physicalAmount || 0);
          return Number(total).toLocaleString();
        },
      },
    ];

    const type = data[0]?.client?.type;

    const personalTaxColumns = [
      {
        Header: '세금',
        id: 'tax',
        Cell: ({ row }) => {
          const total = (row.original.totalAmount || 0) + (row.original.physicalAmount || 0);
          return Math.round(total * 0.033).toLocaleString();
        },
      },
      {
        Header: '실지급액',
        id: 'net',
        Cell: ({ row }) => {
          const total = (row.original.totalAmount || 0) + (row.original.physicalAmount || 0);
          return <b>{Math.round(total * 0.967).toLocaleString()}</b>;
        },
      },
    ];

    const businessTaxColumns = [
      {
        Header: '부가세',
        id: 'vat',
        Cell: ({ row }) => {
          const total = (row.original.totalAmount || 0) + (row.original.physicalAmount || 0);
          return Math.round(total * 0.1).toLocaleString();
        },
      },
      {
        Header: '실지급액',
        id: 'net',
        Cell: ({ row }) => {
          const total = (row.original.totalAmount || 0) + (row.original.physicalAmount || 0);
          return <b>{Math.round(total * 1.1).toLocaleString()}</b>;
        },
      },
    ];

    const payDateColumn = {
      Header: '지급일',
      id: 'payDate',
      Cell: ({ row }) => {
        const m = row.original.calculateMonth;
        if (!m) return '-';
        const d = new Date(m);
        d.setMonth(d.getMonth() + 2);
        d.setDate(0); // 다음달 말일
        return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
      },
    };

    return [
      ...baseColumns,
      ...(type === '개인' ? personalTaxColumns : businessTaxColumns),
      payDateColumn,
    ];
  }, [data, navigate, totalCount, currentPage, pageSize]);

  const sortedData = React.useMemo(() => {
    return [...data].sort((a, b) => {
      return b.calculateMonth.localeCompare(a.calculateMonth);
    });
  }, [data]);

  return (
    <ResponsiveTable
      columns={columns}
      data={sortedData}
      visibleOnMobile={['index', 'calculateMonth', 'amount', 'totalAmount']}
    />
  );
}

export default ByEachClientLatestMonthTable;
